import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';

import { companyHash } from '../../../../../shared/api/action/GeotabActions';
import { saveWexCredentials } from '../../../../../shared/api/action/CustomActions';

const WexLinkCredentialsContainer = () => {
	const dispatch = useDispatch();
	const { companyHash: hash } = useParams();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'geotab.wexLinkCredentialsContainer' });
	const [company, setCompany] = useState(null);
	const [data, setData] = useState({ host: '', username: '', password: '' });
	const [errMessage, setErrMessage] = useState({});
	const [showPassword, setShowPassword] = useState(false);
	const submitting = useRef(false);
	const toggleLoader = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}, [dispatch]);

	useEffect(() => {
		toggleLoader(true);
		setCompany(null);
		setData({ host: '', username: '', password: '' });
		setErrMessage({});
		setShowPassword(false);

		companyHash(hash)
			.then((res) => {
				const resolvedCompany = res.data.response;
				if (!resolvedCompany?.id) {
					toastr.error(t('invalidCompany'));
					return;
				}
				setCompany(resolvedCompany);
				setData({ host: resolvedCompany.wex_host || '', username: resolvedCompany.wex_username || '', password: '' });
			}).catch((error) => {
				toastr.error(error.response?.data?.response?.error || i18n.t('errorMessage.errDescription'));
			}).finally(() => {
				toggleLoader(false);
			});
	}, [hash]);

	const fillForm = (e) => {
		const { name, value } = e.target;
		setData(prev => ({ ...prev, [name]: value }));
		setErrMessage(prev => ({ ...prev, [name]: '' }));
	};

	const checkForm = (params) => {
		const errMessageUpdate = {};
		for (const field of ['host', 'username', 'password']) {
			if (!params[field].trim()) errMessageUpdate[field] = t('required', { field: t(field) });
		}
		setErrMessage(prevState => ({ ...prevState, ...errMessageUpdate }))
		return Object.keys(errMessageUpdate).length > 0;
	};

	const sendForm = async (e) => {
		e.preventDefault();

		if (submitting.current || !company?.id) return;

		const params = {
			host: data.host.trim(),
			username: data.username.trim(),
			password: data.password
		};
		if (checkForm(params)) return;
		submitting.current = true;
		toggleLoader(true);
		try {
			await saveWexCredentials({ ...params, company_id: company.id });
			setData(prev => ({ ...prev, password: '' }));
			setShowPassword(false);
			toastr.success('', t('credentialsSaved'));
		} catch (error) {
			toastr.error(error.response?.data?.response?.error || i18n.t('errorMessage.errDescription'));
		} finally {
			toggleLoader(false);
			submitting.current = false;
		}
	};

	return (
		<div className="panel-body">
			<h3 className="login-header">{t('title')}</h3>
			<div className="form-wrapper">
				<form onSubmit={sendForm} noValidate>
					<fieldset disabled={!company?.id} style={{ minWidth: 0 }}>
						{['host', 'username', 'password'].map(field => (
							<div key={field} className={classnames('form-group', { 'has-error': errMessage[field] })}>
								<label htmlFor={`wex-${field}`} className={classnames('login-label', { active: data[field].length })}>{t(field)}</label>
								<div style={{ position: 'relative' }}>
									<input
										id={`wex-${field}`}
										name={field}
										type={field === 'password' && !showPassword ? 'password' : 'text'}
										className="vehicle-search"
										placeholder={t(field)}
										value={data[field]}
										onChange={fillForm}
										autoComplete={field === 'password' ? 'new-password' : field === 'username' ? 'username' : 'off'}
										aria-invalid={!!errMessage[field]}
										aria-describedby={errMessage[field] ? `wex-${field}-error` : undefined}
										style={field === 'password' ? { paddingRight: '42px' } : undefined}
									/>
									{field === 'password' && (
										<button type="button" title={t(showPassword ? 'hidePassword' : 'showPassword')}
											aria-label={t(showPassword ? 'hidePassword' : 'showPassword')} aria-pressed={showPassword}
											onClick={() => setShowPassword(value => !value)}
											style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', width: '32px', height: '32px', border: 0, background: 'transparent' }}>
											<i className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'} aria-hidden="true" />
										</button>
									)}
								</div>
								{errMessage[field] && <span id={`wex-${field}-error`} className="help-block" role="alert">{errMessage[field]}</span>}
							</div>
						))}
						<div className="form-group submit-btn" style={{ paddingTop: '30px' }}>
							<input
								type="submit"
								className="ui account-btn"
								value={t('save')}
							/>
						</div>
					</fieldset>
				</form>
			</div>
		</div>
	);
};

export default WexLinkCredentialsContainer;
